// "Soluzioni compatibili" (presentational): search options, button, progress and the results table (or the fields
// to fill in first). The search itself (findCompatibleSolutions, auto refresh after the first run) lives in the page.
import { memo, useId, type FC } from "react";
import { Accordion } from "../../../components/ds2/Accordion";
import { Button } from "../../../components/ds2/Button";
import { MessageBox } from "../../../components/ds2/MessageBox";
import { ProgressBar } from "../../../components/ds2/ProgressBar";
import { Switch } from "../../../components/ds2/Switch";
import { Table, TableCell, TableHeadCell, TableRow } from "../../../components/ds2/Table";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import type { SolutionRow } from "../../../http/screwConnector/formulas";
import Html from "./Html";
import FamilyCell from "./FamilyCell";
import styles from "./SolutionsCard.module.css";

export type SolutionsProgress = { processed: number; total: number; found: number };

export type MissingField = { label: string; panel: string; onFocus?: () => void };

export type SolutionsView =
  | { kind: "missing"; fields: MissingField[] }
  | { kind: "done"; candidates: number; found: number; rows: SolutionRow[]; selectNew: boolean };

type SolutionsCardProps = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  predrilled: boolean; // #solPreforo
  washer: boolean; // #solRondella
  onPredrilledChange: (checked: boolean) => void;
  onWasherChange: (checked: boolean) => void;
  running: boolean;
  // null while preparing, before the first candidate is evaluated
  progress: SolutionsProgress | null;
  result: SolutionsView | null;
  onSearch: () => void;
  onApply: (row: SolutionRow) => void;
};

const pct = (v: number | null) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);

const SolutionsCard: FC<SolutionsCardProps> = ({
  collapsed,
  onToggleCollapsed,
  predrilled,
  washer,
  onPredrilledChange,
  onWasherChange,
  running,
  progress,
  result,
  onSearch,
  onApply,
}) => {
  const disabled = useReadOnly();
  const t = useI18n().translation.screwConnectorModule.calculation.solutions;
  const id = useId();

  return (
    <Accordion title={t.title} open={!collapsed} onToggle={onToggleCollapsed}>
      <div className={styles.content}>
        <Html as="p" text={t.intro} className={styles.intro} />
        <div className={styles.options}>
          <Switch
            id={`${id}-predrilled`}
            data-tut="solPreforo"
            checked={predrilled}
            disabled={disabled}
            onChange={onPredrilledChange}
          >
            {t.predrilled}
          </Switch>
          <Switch id={`${id}-washer`} checked={washer} disabled={disabled} onChange={onWasherChange}>
            {t.washer}
          </Switch>
          <span>{t.optionsHint}</span>
        </div>
        <Button
          variant="secondary"
          data-tut="findSolutions"
          disabled={disabled || running}
          onClick={onSearch}
        >
          {t.search}
        </Button>
        {running ? (
          <ProgressBar
            value={progress && progress.total > 0 ? (progress.processed / progress.total) * 100 : 0}
            message={progress ? t.progress(progress.processed, progress.total, progress.found) : t.preparing}
          />
        ) : null}
        {result ? (
          <div className={styles.results}>
            {result.kind === "missing" ? (
              <MessageBox type="error">
                <p className={styles.missingTitle}>{t.missingTitle(result.fields.length)}</p>
                <ul className={styles.missingList}>
                  {result.fields.map((field, i) => (
                    <li key={i}>
                      <Button variant="plain" onClick={field.onFocus}>
                        {field.label}
                      </Button>{" "}
                      {t.missingPanel(field.panel)}
                    </li>
                  ))}
                </ul>
              </MessageBox>
            ) : result.found === 0 ? (
              <MessageBox type="warning">{t.none(result.candidates)}</MessageBox>
            ) : (
              <>
                {result.selectNew ? (
                  <MessageBox type="warning">
                    <Html text={t.selectNew} />
                  </MessageBox>
                ) : null}
                <p className={styles.summary}>{t.summary(result.found, result.candidates, result.rows.length)}</p>
                <Table dense>
                  <thead>
                    <tr>
                      <TableHeadCell>#</TableHeadCell>
                      <TableHeadCell>{t.columns.family}</TableHeadCell>
                      <TableHeadCell>d</TableHeadCell>
                      <TableHeadCell>L</TableHeadCell>
                      <TableHeadCell>{t.columns.grid}</TableHeadCell>
                      <TableHeadCell>{t.columns.options}</TableHeadCell>
                      <TableHeadCell>{t.columns.shear}</TableHeadCell>
                      <TableHeadCell>{t.columns.combined}</TableHeadCell>
                      <TableHeadCell>{t.columns.withdrawal}</TableHeadCell>
                      <TableHeadCell />
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <TableRow key={`${row.family}-${row.diametro}-${row.lunghezza}-${row.rondella ?? ""}`}>
                        <TableCell noWrap>{i + 1}</TableCell>
                        <TableCell noWrap>
                          <FamilyCell family={row.family} inTable />
                        </TableCell>
                        <TableCell noWrap>{row.diametro.toFixed(1).replace(/\.0$/, "")} mm</TableCell>
                        <TableCell noWrap>{row.lunghezza.toFixed(0)} mm</TableCell>
                        <TableCell noWrap>
                          {row.rows}×{row.cols} ({row.totale})
                        </TableCell>
                        <TableCell noWrap>
                          {[row.foro === "predrilled" ? t.predrilledOption : null, row.rondella]
                            .filter(Boolean)
                            .join(" + ") || "—"}
                        </TableCell>
                        <TableCell noWrap>{pct(row.etaTaglio)}</TableCell>
                        <TableCell noWrap>{pct(row.etaCombinata)}</TableCell>
                        <TableCell noWrap>{pct(row.etaAxSimple)}</TableCell>
                        <TableCell noWrap>
                          <Button variant="secondary" disabled={disabled} onClick={() => onApply(row)}>
                            {t.apply}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        ) : null}
      </div>
    </Accordion>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(SolutionsCard);
